/**
 * Tax Rep telephone: `(852)37569536` style.
 * Supported area codes ? local digit length (grill-me Q1 A+B / Q2 A / Q6 A).
 */

export const TAX_REP_DEFAULT_AREA = '852';

/** Area code (no +) ? required local digit count. */
export const TAX_REP_AREA_LOCAL_LEN: Readonly<Record<string, number>> = {
    '852': 8,
    '86': 11,
    '853': 8,
    '886': 9,
};

/** Longest-first so 886 / 852 match before shorter prefixes. */
const AREA_CODES_DESC = Object.keys(TAX_REP_AREA_LOCAL_LEN).sort((a, b) => b.length - a.length);

export function localLenForArea(area: string): number | undefined {
    return TAX_REP_AREA_LOCAL_LEN[area];
}

export function defaultTaxRepPhone(): string {
    return `(${TAX_REP_DEFAULT_AREA})`;
}

/**
 * Normalize stored / pasted values into `(area)local`.
 * Unrecognized ? `(852)` + trailing digits capped to HK length.
 */
export function normalizeTaxRepPhone(raw: string | null | undefined): string {
    if (raw == null || !String(raw).trim()) {
        return defaultTaxRepPhone();
    }
    const s = String(raw).trim();

    const paren = s.match(/^\(\s*(\d{1,4})\s*\)\s*([\d\s\-]*)$/);
    if (paren) {
        const area = paren[1];
        const max = localLenForArea(area);
        const localDigits = paren[2].replace(/\D/g, '');
        const local = max != null ? localDigits.slice(0, max) : localDigits.slice(0, 11);
        return `(${area})${local}`;
    }

    const digits = s.replace(/\D/g, '');
    if (!digits) {
        return defaultTaxRepPhone();
    }

    for (const code of AREA_CODES_DESC) {
        if (digits.startsWith(code)) {
            const max = TAX_REP_AREA_LOCAL_LEN[code];
            const local = digits.slice(code.length).slice(0, max);
            return `(${code})${local}`;
        }
    }

    // Unknown prefix: keep as incomplete area (?4) or fall back to HK + remainder
    if (digits.length <= 4) {
        return `(${digits})`;
    }
    const max = TAX_REP_AREA_LOCAL_LEN[TAX_REP_DEFAULT_AREA];
    return `(${TAX_REP_DEFAULT_AREA})${digits.slice(0, max)}`;
}

/**
 * Live typing sanitizer: digits only ? `(area)local` with length caps.
 */
export function sanitizeTaxRepPhoneInput(raw: string): string {
    const digits = String(raw ?? '').replace(/\D/g, '');
    if (!digits) {
        return defaultTaxRepPhone();
    }

    for (const code of AREA_CODES_DESC) {
        if (digits.startsWith(code)) {
            const max = TAX_REP_AREA_LOCAL_LEN[code];
            const local = digits.slice(code.length).slice(0, max);
            return `(${code})${local}`;
        }
    }

    // Typing area code (e.g. "8", "86", "852") � keep in parentheses until a known code completes
    if (digits.length <= 4) {
        const maybeKnown = AREA_CODES_DESC.find(c => c.startsWith(digits) || digits.startsWith(c));
        if (maybeKnown && digits.startsWith(maybeKnown)) {
            const max = TAX_REP_AREA_LOCAL_LEN[maybeKnown];
            return `(${maybeKnown})${digits.slice(maybeKnown.length).slice(0, max)}`;
        }
        return `(${digits})`;
    }

    const max = TAX_REP_AREA_LOCAL_LEN[TAX_REP_DEFAULT_AREA];
    return `(${TAX_REP_DEFAULT_AREA})${digits.slice(0, max)}`;
}

/** True when area is supported and local length matches the table. */
export function isCompleteTaxRepPhone(raw: string | null | undefined): boolean {
    const n = normalizeTaxRepPhone(raw);
    const m = n.match(/^\((\d+)\)(\d+)$/);
    if (!m) return false;
    const max = localLenForArea(m[1]);
    return max != null && m[2].length === max;
}

/** Has more than bare `(area)` � used for required-field checks. */
export function hasTaxRepPhoneLocal(raw: string | null | undefined): boolean {
    const n = normalizeTaxRepPhone(raw);
    const m = n.match(/^\((\d+)\)(\d*)$/);
    return !!(m && m[2].length > 0);
}
